import { useEffect, useLayoutEffect, useMemo, useState } from "react";

export type AgentActivityDisplay = "working" | "finished" | "idle";

export const AGENT_FINISHED_WINDOW_MS = 300_000;

const workingStatuses = new Set(["generating", "running_tool"]);
const finishedStatuses = new Set(["finished", "awaiting_input"]);

/**
 * Derive presentation activity from the durable status heartbeat. A terminal
 * outcome remains in its transcript/error frame; activity only says when work
 * most recently stopped.
 */
export function resolveAgentActivityDisplay(
  status: string | null | undefined,
  lastHeartbeatAt: string | null | undefined,
  now = Date.now(),
): AgentActivityDisplay {
  if (workingStatuses.has(status ?? "")) return "working";
  if (!finishedStatuses.has(status ?? "")) return "idle";
  const heartbeat = typeof lastHeartbeatAt === "string" ? Date.parse(lastHeartbeatAt) : NaN;
  return Number.isFinite(heartbeat) && heartbeat <= now && now - heartbeat < AGENT_FINISHED_WINDOW_MS
    ? "finished"
    : "idle";
}

export function getAgentActivityExpiry(
  status: string | null | undefined,
  lastHeartbeatAt: string | null | undefined,
  now = Date.now(),
): number | null {
  if (!finishedStatuses.has(status ?? "")) return null;
  const heartbeat = typeof lastHeartbeatAt === "string" ? Date.parse(lastHeartbeatAt) : NaN;
  return Number.isFinite(heartbeat) && heartbeat <= now
    ? heartbeat + AGENT_FINISHED_WINDOW_MS
    : null;
}

/**
 * One deadline clock for a rendered agent collection. Consumers resolve all
 * strip/header/chat activity against this same value, so the five-minute
 * transition is not delayed until the next polling refresh.
 */
export function useAgentActivityClock(
  agents: ReadonlyArray<{
    agentStatus?: string | null;
    agentLastHeartbeatAt?: string | null;
  }>,
): number {
  const [now, setNow] = useState(() => Date.now());
  // A status frame can arrive after this hook's initial render. Refresh the
  // shared clock before paint whenever the authoritative status/heartbeat
  // inputs change, otherwise a newer finished heartbeat looks "future" to the
  // mount-time clock and never schedules its expiry.
  const statusInputKey = useMemo(
    () => agents
      .map((agent) => `${agent.agentStatus ?? ""}\u0000${agent.agentLastHeartbeatAt ?? ""}`)
      .join("\u0001"),
    [agents],
  );

  useLayoutEffect(() => {
    setNow(Date.now());
  }, [statusInputKey]);

  const deadlineKey = agents
    .map((agent) => getAgentActivityExpiry(agent.agentStatus, agent.agentLastHeartbeatAt, now))
    .filter((deadline): deadline is number => deadline !== null && deadline > now)
    .sort((left, right) => left - right)[0] ?? null;

  useEffect(() => {
    if (deadlineKey === null) return undefined;
    const timer = window.setTimeout(() => setNow(Date.now()), deadlineKey - now);
    return () => window.clearTimeout(timer);
  }, [deadlineKey, now]);

  return now;
}
